/* AISummaryPipelinesPanel.jsx — Feb 2026
 *
 * Super-admin-only Settings surface for authoring the AI Summary
 * Pipeline that drives the Rep Activity → Review lightbox summary.
 *
 * Pipelines are branching graphs of prompt nodes:
 *   - Classifier nodes emit an enum label → routes decide next node
 *     (parallel branching).
 *   - Summarizer nodes emit the terminal JSON contract
 *     {headline, what_happened[], risks[], next_actions[]}.
 *     They can chain sequentially (single default route) OR terminate
 *     (empty routes).
 *
 * Two views:
 *   • List view: all pipelines for scope call_review — Activate,
 *     Duplicate, Edit, Publish, Delete, View Runs.
 *   • Editor view: per-pipeline node list + prompt editor + routes
 *     table + Dry-Run panel with per-node trace.
 *
 * Backend endpoints (all super_admin gated):
 *   GET    /api/admin/ai-pipelines
 *   GET    /api/admin/ai-pipelines/{id}
 *   POST   /api/admin/ai-pipelines
 *   PATCH  /api/admin/ai-pipelines/{id}
 *   POST   /api/admin/ai-pipelines/{id}/duplicate
 *   POST   /api/admin/ai-pipelines/{id}/publish
 *   POST   /api/admin/ai-pipelines/{id}/activate
 *   DELETE /api/admin/ai-pipelines/{id}
 *   POST   /api/admin/ai-pipelines/dry-run
 *   GET    /api/admin/ai-pipelines/{id}/runs
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import {
  Sparkles, Plus, Copy, Edit3, Trash2, Play, CheckCircle2, Circle,
  ChevronLeft, Save, AlertTriangle, Zap, GitBranch, ChevronRight,
  Loader2, Info, Send, LayoutGrid, Network,
} from "lucide-react";
import ReactFlow, {
  Background, Controls, MarkerType, Handle, Position,
} from "reactflow";
import "reactflow/dist/style.css";

const SCOPE = "call_review";

// Available context variables (surface to admin as pill hints)
const CTX_VARS = [
  "context", "transcript", "rep_name", "lead_name", "outcome",
  "stage", "substatus", "sentiment", "next_step",
  "disposition_notes", "quick_notes", "ai_call_takeaway",
  "call_outcome_ai", "ai_key_points", "ai_action_items",
];

// ═══════════════════════════════════════════════════════════════════
// TOP-LEVEL
// ═══════════════════════════════════════════════════════════════════
export default function AISummaryPipelinesPanel() {
  const { token } = useAuth();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [view, setView] = useState({ mode: "list" });
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/ai-pipelines`, {
        params: { scope: SCOPE }, headers: authHeaders,
      });
      setPipelines(data.pipelines || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load pipelines");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { reload(); }, [reload]);

  if (view.mode === "edit") {
    return (
      <PipelineEditor
        pipelineId={view.id}
        onClose={() => { setView({ mode: "list" }); reload(); }}
        authHeaders={authHeaders}
      />
    );
  }

  return (
    <>
      <PipelineList
        pipelines={pipelines}
        loading={loading}
        reload={reload}
        onEdit={(id) => setView({ mode: "edit", id })}
        onNew={() => setShowTemplatePicker(true)}
        authHeaders={authHeaders}
      />
      {showTemplatePicker && (
        <TemplatePickerModal
          authHeaders={authHeaders}
          onClose={() => setShowTemplatePicker(false)}
          onCreated={async (newDoc) => {
            setShowTemplatePicker(false);
            await reload();
            setView({ mode: "edit", id: newDoc.id });
          }}
        />
      )}
    </>
  );
}


// ═══════════════════════════════════════════════════════════════════
// LIST VIEW
// ═══════════════════════════════════════════════════════════════════
function PipelineList({ pipelines, loading, reload, onEdit, onNew, authHeaders }) {
  const [busy, setBusy] = useState(null);

  const doAction = async (verb, id, url, method = "post") => {
    setBusy(id + verb);
    try {
      const cfg = { headers: authHeaders };
      const req = method === "delete"
        ? axios.delete(url, cfg)
        : axios[method](url, {}, cfg);
      await req;
      toast.success(`Pipeline ${verb}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    } finally {
      setBusy(null);
    }
  };

  const activate = (p) => doAction("activated", p.id, `${API}/admin/ai-pipelines/${p.id}/activate`);
  const duplicate = (p) => doAction("duplicated", p.id, `${API}/admin/ai-pipelines/${p.id}/duplicate`);
  const publish = (p) => doAction("published", p.id, `${API}/admin/ai-pipelines/${p.id}/publish`);
  const remove = (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    doAction("deleted", p.id, `${API}/admin/ai-pipelines/${p.id}`, "delete");
  };

  return (
    <div data-testid="ai-summary-pipelines-panel">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 650, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={18} style={{ color: "#5856D6" }} /> AI Summary Pipelines
          </h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--text-tertiary)", maxWidth: 720, lineHeight: 1.5 }}>
            Design the branching prompt graph that generates the Rep Activity → Review AI summary.
            Chain classifier + summarizer nodes into hierarchies or parallel branches. The active pipeline
            runs whenever a manager opens the Review lightbox. Old cached summaries are preserved;
            managers can &quot;Regenerate&quot; any specific call to re-run it under the new pipeline.
          </p>
        </div>
        <button
          data-testid="ai-pipelines-new-btn"
          onClick={onNew}
          disabled={loading}
          style={btnPrimary}
        >
          <Plus size={14} /> New Pipeline
        </button>
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: "center", color: "#86868b" }}>
          <Loader2 size={16} style={{ animation: "spin 1s linear infinite", display: "inline-block" }} /> Loading…
        </div>
      )}

      {!loading && pipelines.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: "#86868b", border: "1px dashed #e5e5ea", borderRadius: 10 }}>
          No pipelines yet.
        </div>
      )}

      {!loading && pipelines.length > 0 && (
        <div style={{ display: "grid", gap: 10 }}>
          {pipelines.map((p) => (
            <div
              key={p.id}
              data-testid={`ai-pipeline-row-${p.id}`}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gap: 14,
                alignItems: "center",
                padding: "14px 16px",
                background: "#fff",
                border: p.is_active ? "1px solid #34C759" : "0.5px solid #e5e5ea",
                borderRadius: 10,
                boxShadow: p.is_active ? "0 0 0 2px rgba(52,199,89,0.10)" : "none",
              }}
            >
              <div style={{ width: 6, height: 40, borderRadius: 3, background: p.is_active ? "#34C759" : "#e5e5ea" }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                  <StatusBadge status={p.status} isActive={p.is_active} />
                  {p.is_system_default && (
                    <span style={{ fontSize: 10, color: "#5856D6", fontWeight: 600, background: "#EEF0FF", padding: "2px 7px", borderRadius: 4 }}>
                      SYSTEM DEFAULT
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: "#86868b" }}>v{p.version} · {(p.nodes || []).length} node{(p.nodes || []).length === 1 ? "" : "s"}</span>
                </div>
                {p.description && (
                  <div style={{ fontSize: 12, color: "#475569", marginTop: 4, lineHeight: 1.4, maxWidth: 640 }}>
                    {p.description}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {!p.is_active && p.status === "published" && (
                  <button data-testid={`ai-pipeline-activate-${p.id}`} disabled={busy === p.id + "activated"} onClick={() => activate(p)} style={btnSuccess}>
                    <CheckCircle2 size={12} /> Activate
                  </button>
                )}
                {p.status === "draft" && (
                  <button data-testid={`ai-pipeline-publish-${p.id}`} disabled={busy === p.id + "published"} onClick={() => publish(p)} style={btnAccent}>
                    <Send size={12} /> Publish
                  </button>
                )}
                <button data-testid={`ai-pipeline-edit-${p.id}`} onClick={() => onEdit(p.id)} style={btnGhost}>
                  {p.is_system_default || p.status === "published" ? <><Info size={12} /> View</> : <><Edit3 size={12} /> Edit</>}
                </button>
                <button data-testid={`ai-pipeline-duplicate-${p.id}`} disabled={busy === p.id + "duplicated"} onClick={() => duplicate(p)} style={btnGhost}>
                  <Copy size={12} /> Duplicate
                </button>
                {!p.is_system_default && !p.is_active && (
                  <button data-testid={`ai-pipeline-delete-${p.id}`} disabled={busy === p.id + "deleted"} onClick={() => remove(p)} style={{ ...btnGhost, color: "#c9302c" }}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// EDITOR VIEW
// ═══════════════════════════════════════════════════════════════════
function PipelineEditor({ pipelineId, onClose, authHeaders }) {
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [leftMode, setLeftMode] = useState("list"); // "list" | "graph"
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dryRun, setDryRun] = useState({ open: false, transcript: "", result: null, loading: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/ai-pipelines/${pipelineId}`, { headers: authHeaders });
        if (!alive) return;
        setPipeline(data);
        setSelectedNodeId((data.nodes || [])[0]?.id || null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load pipeline");
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [pipelineId, authHeaders, onClose]);

  const readOnly = pipeline?.is_system_default || pipeline?.status === "published";

  const patchPipeline = (patch) => {
    setPipeline((p) => ({ ...p, ...patch }));
    setDirty(true);
  };
  const patchNode = (nodeId, patch) => {
    setPipeline((p) => ({
      ...p,
      nodes: p.nodes.map((n) => (n.id === nodeId ? { ...n, ...patch } : n)),
    }));
    setDirty(true);
  };
  const addNode = () => {
    const id = "n_" + Math.random().toString(36).slice(2, 8);
    const newNode = {
      id, name: "New Node", kind: "classifier",
      model: "gpt-5-mini",
      system_prompt: "You are a classifier. Read the transcript and output one label.",
      user_prompt_template: "Transcript:\n{{transcript}}",
      output_schema: { type: "enum", values: ["a", "b"] },
      routes: [{ default: true, to: pipeline.nodes[0]?.id }],
    };
    setPipeline((p) => ({ ...p, nodes: [...p.nodes, newNode] }));
    setSelectedNodeId(id);
    setDirty(true);
  };
  const removeNode = (nodeId) => {
    if (pipeline.nodes.length <= 1) { toast.error("A pipeline must have at least one node."); return; }
    if (pipeline.entry_node_id === nodeId) { toast.error("This is the entry node. Change the entry node first."); return; }
    setPipeline((p) => ({
      ...p,
      nodes: p.nodes
        .filter((n) => n.id !== nodeId)
        .map((n) => ({ ...n, routes: (n.routes || []).filter((r) => r.to !== nodeId) })),
    }));
    setSelectedNodeId(pipeline.nodes[0]?.id);
    setDirty(true);
  };

  const save = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/ai-pipelines/${pipelineId}`, {
        name: pipeline.name,
        description: pipeline.description || "",
        scope: pipeline.scope,
        entry_node_id: pipeline.entry_node_id,
        nodes: pipeline.nodes,
      }, { headers: authHeaders });
      toast.success("Draft saved");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runDryRun = async () => {
    if (!dryRun.transcript.trim()) { toast.error("Paste a transcript first"); return; }
    setDryRun((d) => ({ ...d, loading: true, result: null }));
    try {
      const { data } = await axios.post(`${API}/admin/ai-pipelines/dry-run`, {
        pipeline,
        transcript: dryRun.transcript,
      }, { headers: authHeaders });
      setDryRun((d) => ({ ...d, loading: false, result: data }));
    } catch (e) {
      setDryRun((d) => ({ ...d, loading: false, result: { error: e?.response?.data?.detail || "Dry run failed" } }));
    }
  };

  if (loading || !pipeline) {
    return <div style={{ padding: 40, textAlign: "center" }}><Loader2 style={{ animation: "spin 1s linear infinite" }} /></div>;
  }

  const selectedNode = pipeline.nodes.find((n) => n.id === selectedNodeId) || pipeline.nodes[0];

  return (
    <div data-testid="ai-pipeline-editor">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <button data-testid="ai-pipeline-editor-back" onClick={onClose} style={btnGhost}>
          <ChevronLeft size={14} /> Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          {readOnly ? (
            <div style={{ fontSize: 16, fontWeight: 650 }}>{pipeline.name}</div>
          ) : (
            <input
              data-testid="ai-pipeline-name-input"
              value={pipeline.name}
              onChange={(e) => patchPipeline({ name: e.target.value })}
              style={{ ...input, fontSize: 15, fontWeight: 600, width: "100%", maxWidth: 500 }}
            />
          )}
          <div style={{ fontSize: 11, color: "#86868b", marginTop: 3 }}>
            <StatusBadge status={pipeline.status} isActive={pipeline.is_active} />
            <span style={{ marginLeft: 6 }}>v{pipeline.version} · Scope: {pipeline.scope}</span>
            {readOnly && <span style={{ marginLeft: 6, color: "#c9302c" }}>· Read-only (Duplicate to iterate)</span>}
          </div>
        </div>
        {!readOnly && (
          <button data-testid="ai-pipeline-save-btn" onClick={save} disabled={!dirty || saving} style={dirty ? btnPrimary : btnGhost}>
            {saving ? <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} /> : <Save size={12} />}
            {dirty ? " Save draft" : " Saved"}
          </button>
        )}
      </div>

      {/* Description */}
      {!readOnly ? (
        <textarea
          data-testid="ai-pipeline-description-input"
          value={pipeline.description || ""}
          onChange={(e) => patchPipeline({ description: e.target.value })}
          rows={2}
          placeholder="What is this pipeline for? (optional)"
          style={{ ...input, width: "100%", marginBottom: 12, resize: "vertical" }}
        />
      ) : pipeline.description ? (
        <div style={{ fontSize: 13, color: "#475569", marginBottom: 12, padding: "10px 12px", background: "#f8fafc", borderRadius: 6 }}>
          {pipeline.description}
        </div>
      ) : null}

      {/* Body: 3-column layout */}
      <div style={{ display: "grid", gridTemplateColumns: leftMode === "graph" ? "520px 1fr 320px" : "260px 1fr 320px", gap: 12, minHeight: 500 }}>
        {/* LEFT: node list OR graph view */}
        <div style={{ background: "#fff", border: "0.5px solid #e5e5ea", borderRadius: 10, padding: leftMode === "graph" ? 0 : 10, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: leftMode === "graph" ? "10px 12px" : "0 0 8px 0", borderBottom: leftMode === "graph" ? "0.5px solid #e5e5ea" : "0" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#86868b", letterSpacing: "0.04em" }}>Nodes</div>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {/* View toggle — Feb 2026 (Phase 3 visual canvas). List
                  view is the compact editable rail; Graph view uses
                  react-flow to show node-and-edge topology. Clicking
                  any node in either view selects it in the center
                  pane, so behaviour is identical — this is purely a
                  visualisation swap. */}
              <button
                data-testid="ai-pipeline-view-list"
                onClick={() => setLeftMode("list")}
                title="List view"
                style={{
                  padding: "3px 6px", fontSize: 10, border: "0.5px solid #e5e5ea",
                  borderRadius: 4, background: leftMode === "list" ? "#EEF0FF" : "#fff",
                  color: leftMode === "list" ? "#5856D6" : "#475569",
                  cursor: "pointer", fontFamily: "inherit",
                }}
              ><LayoutGrid size={11} /></button>
              <button
                data-testid="ai-pipeline-view-graph"
                onClick={() => setLeftMode("graph")}
                title="Graph view"
                style={{
                  padding: "3px 6px", fontSize: 10, border: "0.5px solid #e5e5ea",
                  borderRadius: 4, background: leftMode === "graph" ? "#EEF0FF" : "#fff",
                  color: leftMode === "graph" ? "#5856D6" : "#475569",
                  cursor: "pointer", fontFamily: "inherit",
                }}
              ><Network size={11} /></button>
              {!readOnly && leftMode === "list" && (
                <button data-testid="ai-pipeline-add-node" onClick={addNode} style={{ ...btnGhost, padding: "3px 6px" }}>
                  <Plus size={11} />
                </button>
              )}
            </div>
          </div>

          {leftMode === "graph" ? (
            <PipelineGraphView
              pipeline={pipeline}
              selectedNodeId={selectedNodeId}
              onSelect={setSelectedNodeId}
            />
          ) : (
            <>
          <div style={{ display: "grid", gap: 4 }}>
            {pipeline.nodes.map((n) => {
              const isEntry = pipeline.entry_node_id === n.id;
              const isSelected = selectedNodeId === n.id;
              return (
                <button
                  key={n.id}
                  data-testid={`ai-pipeline-node-select-${n.id}`}
                  onClick={() => setSelectedNodeId(n.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "8px 10px",
                    borderRadius: 6, cursor: "pointer", textAlign: "left",
                    background: isSelected ? "#EEF0FF" : "transparent",
                    border: isSelected ? "0.5px solid #5856D6" : "0.5px solid transparent",
                    fontFamily: "inherit", fontSize: 12,
                  }}
                >
                  {n.kind === "classifier" ? <GitBranch size={12} style={{ color: "#0071E3" }} /> : <Zap size={12} style={{ color: "#34C759" }} />}
                  <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {n.name}
                  </div>
                  {isEntry && <span title="Entry node" style={{ fontSize: 9, color: "#5856D6", fontWeight: 700 }}>▶</span>}
                  {(n.routes || []).length === 0 && n.kind === "summarizer" && (
                    <span title="Terminal" style={{ fontSize: 9, color: "#34C759", fontWeight: 700 }}>■</span>
                  )}
                </button>
              );
            })}
          </div>

          {!readOnly && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "0.5px dashed #e5e5ea" }}>
              <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#86868b" }}>Entry node</label>
              <select
                data-testid="ai-pipeline-entry-select"
                value={pipeline.entry_node_id}
                onChange={(e) => patchPipeline({ entry_node_id: e.target.value })}
                style={{ ...input, width: "100%", marginTop: 4 }}
              >
                {pipeline.nodes.map((n) => (
                  <option key={n.id} value={n.id}>{n.name}</option>
                ))}
              </select>
            </div>
          )}
            </>
          )}
        </div>

        {/* CENTER: node editor */}
        <div style={{ background: "#fff", border: "0.5px solid #e5e5ea", borderRadius: 10, padding: 16, overflow: "auto" }}>
          {selectedNode ? (
            <NodeEditor
              node={selectedNode}
              readOnly={readOnly}
              otherNodes={pipeline.nodes.filter((n) => n.id !== selectedNode.id)}
              onPatch={(patch) => patchNode(selectedNode.id, patch)}
              onRemove={() => removeNode(selectedNode.id)}
            />
          ) : (
            <div style={{ color: "#86868b", fontSize: 13 }}>Select a node.</div>
          )}
        </div>

        {/* RIGHT: dry-run panel */}
        <div style={{ background: "#fff", border: "0.5px solid #e5e5ea", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Play size={12} style={{ color: "#5856D6" }} />
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#86868b", letterSpacing: "0.04em" }}>Dry Run</div>
          </div>
          <div style={{ fontSize: 11, color: "#86868b", lineHeight: 1.4 }}>
            Paste a sample transcript. Runs the pipeline exactly as it would in production, but does NOT persist the result.
          </div>
          <textarea
            data-testid="ai-pipeline-dryrun-transcript"
            value={dryRun.transcript}
            onChange={(e) => setDryRun((d) => ({ ...d, transcript: e.target.value }))}
            placeholder="Rep: Hi, this is Alex from...&#10;Prospect: Yes, go ahead..."
            rows={6}
            style={{ ...input, width: "100%", fontFamily: "monospace", fontSize: 11, resize: "vertical" }}
          />
          <button
            data-testid="ai-pipeline-dryrun-btn"
            onClick={runDryRun}
            disabled={dryRun.loading || !dryRun.transcript.trim()}
            style={btnPrimary}
          >
            {dryRun.loading ? <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} /> : <Play size={12} />}
            Run
          </button>
          {dryRun.result && <DryRunResult result={dryRun.result} />}
        </div>
      </div>

      {/* Recent runs — Phase 3 addition. Backend endpoint
          /api/admin/ai-pipelines/{id}/runs was already provisioned in
          Phase 1 but had no frontend surface; this section renders
          the last 50 executions with path + latency for audit. */}
      <PipelineRunsSection pipelineId={pipelineId} authHeaders={authHeaders} />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// NODE EDITOR
// ═══════════════════════════════════════════════════════════════════
function NodeEditor({ node, readOnly, otherNodes, onPatch, onRemove }) {
  const enumValues = node.output_schema?.values || [];

  const updateEnumValues = (values) => {
    onPatch({ output_schema: { ...(node.output_schema || {}), type: "enum", values } });
  };

  const addRoute = () => {
    onPatch({
      routes: [
        ...(node.routes || []).filter((r) => !r.default),
        { when: "", to: otherNodes[0]?.id || node.id },
        ...(node.routes || []).filter((r) => r.default),
      ],
    });
  };

  const setRoute = (idx, patch) => {
    const routes = [...(node.routes || [])];
    routes[idx] = { ...routes[idx], ...patch };
    onPatch({ routes });
  };
  const removeRoute = (idx) => {
    const routes = [...(node.routes || [])];
    routes.splice(idx, 1);
    onPatch({ routes });
  };

  const setKind = (kind) => {
    if (kind === "summarizer") {
      onPatch({
        kind: "summarizer",
        output_schema: { type: "object", required: ["headline", "what_happened", "risks", "next_actions"] },
        routes: [],
      });
    } else {
      onPatch({
        kind: "classifier",
        output_schema: { type: "enum", values: node.output_schema?.values || ["a", "b"] },
        routes: (node.routes || []).length ? node.routes : [{ default: true, to: otherNodes[0]?.id || node.id }],
      });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Node name</label>
          <input
            data-testid="ai-node-name"
            value={node.name}
            disabled={readOnly}
            onChange={(e) => onPatch({ name: e.target.value })}
            style={{ ...input, width: "100%" }}
          />
        </div>
        <div>
          <label style={lbl}>Kind</label>
          <select
            data-testid="ai-node-kind"
            value={node.kind}
            disabled={readOnly}
            onChange={(e) => setKind(e.target.value)}
            style={input}
          >
            <option value="classifier">Classifier</option>
            <option value="summarizer">Summarizer</option>
          </select>
        </div>
        {!readOnly && (
          <button data-testid="ai-node-remove" onClick={onRemove} style={{ ...btnGhost, color: "#c9302c" }}>
            <Trash2 size={12} />
          </button>
        )}
      </div>

      <div style={{ padding: "8px 12px", background: "#f8fafc", borderRadius: 6, fontSize: 11, color: "#475569", lineHeight: 1.5 }}>
        <strong>Available variables:</strong>{" "}
        {CTX_VARS.map((v) => (
          <code key={v} style={{ fontSize: 10, background: "#fff", padding: "1px 5px", borderRadius: 3, marginRight: 4, border: "0.5px solid #e5e5ea" }}>{`{{${v}}}`}</code>
        ))}
        <div style={{ marginTop: 4, fontSize: 10 }}>
          Reference upstream classifier/summarizer output with{" "}
          <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 3 }}>{"{{previous.NODE_ID}}"}</code>
        </div>
      </div>

      <div>
        <label style={lbl}>System prompt</label>
        <textarea
          data-testid="ai-node-system-prompt"
          value={node.system_prompt || ""}
          disabled={readOnly}
          onChange={(e) => onPatch({ system_prompt: e.target.value })}
          rows={8}
          style={{ ...input, width: "100%", fontFamily: "monospace", fontSize: 12, resize: "vertical" }}
        />
      </div>

      <div>
        <label style={lbl}>User prompt template</label>
        <textarea
          data-testid="ai-node-user-prompt"
          value={node.user_prompt_template || ""}
          disabled={readOnly}
          onChange={(e) => onPatch({ user_prompt_template: e.target.value })}
          rows={4}
          style={{ ...input, width: "100%", fontFamily: "monospace", fontSize: 12, resize: "vertical" }}
        />
      </div>

      {node.kind === "classifier" && (
        <>
          <div>
            <label style={lbl}>Output categories (comma-separated)</label>
            <input
              data-testid="ai-node-enum-values"
              value={(enumValues || []).join(", ")}
              disabled={readOnly}
              onChange={(e) =>
                updateEnumValues(
                  e.target.value.split(",").map((v) => v.trim()).filter(Boolean),
                )
              }
              style={{ ...input, width: "100%" }}
              placeholder="voicemail, warm, cold"
            />
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>Routes</label>
              {!readOnly && (
                <button data-testid="ai-node-add-route" onClick={addRoute} style={{ ...btnGhost, padding: "3px 6px" }}>
                  <Plus size={11} /> Add
                </button>
              )}
            </div>
            <div style={{ display: "grid", gap: 4 }}>
              {(node.routes || []).map((r, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr auto", gap: 6, alignItems: "center" }}>
                  {r.default ? (
                    <div style={{ padding: "6px 10px", background: "#fef3c7", borderRadius: 4, fontSize: 11, fontWeight: 600, color: "#92400e" }}>
                      Default fallback
                    </div>
                  ) : (
                    <input
                      data-testid={`ai-route-when-${i}`}
                      value={r.when || ""}
                      disabled={readOnly}
                      onChange={(e) => setRoute(i, { when: e.target.value })}
                      placeholder="when output ="
                      style={input}
                    />
                  )}
                  <ChevronRight size={12} style={{ color: "#86868b" }} />
                  <select
                    data-testid={`ai-route-to-${i}`}
                    value={r.to || ""}
                    disabled={readOnly}
                    onChange={(e) => setRoute(i, { to: e.target.value })}
                    style={input}
                  >
                    {otherNodes.map((n) => (
                      <option key={n.id} value={n.id}>{n.name}</option>
                    ))}
                  </select>
                  {!readOnly && !r.default && (
                    <button data-testid={`ai-route-remove-${i}`} onClick={() => removeRoute(i)} style={{ ...btnGhost, padding: "3px 6px", color: "#c9302c" }}>
                      <Trash2 size={11} />
                    </button>
                  )}
                  {r.default && <div />}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {node.kind === "summarizer" && (
        <div>
          <label style={lbl}>After this node</label>
          <select
            data-testid="ai-node-summarizer-next"
            value={(node.routes || [])[0]?.to || ""}
            disabled={readOnly}
            onChange={(e) => {
              const v = e.target.value;
              onPatch({ routes: v === "__terminal__" ? [] : [{ default: true, to: v }] });
            }}
            style={{ ...input, width: "100%" }}
          >
            <option value="__terminal__">Terminate (return this output as final)</option>
            {otherNodes.map((n) => (
              <option key={n.id} value={n.id}>Continue to → {n.name}</option>
            ))}
          </select>
          <div style={{ fontSize: 10, color: "#86868b", marginTop: 4 }}>
            A terminal summarizer returns {`{headline, what_happened[], risks[], next_actions[]}`} as the final Review-lightbox output.
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// DRY-RUN RESULT
// ═══════════════════════════════════════════════════════════════════
function DryRunResult({ result }) {
  if (result.error) {
    return (
      <div style={{ padding: 10, background: "#fee", borderRadius: 6, fontSize: 11, color: "#c9302c", display: "flex", gap: 6 }}>
        <AlertTriangle size={12} /> {result.error}
      </div>
    );
  }
  return (
    <div data-testid="ai-pipeline-dryrun-result" style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#86868b", textTransform: "uppercase" }}>
        Path
      </div>
      <div style={{ fontSize: 11, fontFamily: "monospace", background: "#f8fafc", padding: 8, borderRadius: 6, wordBreak: "break-all" }}>
        {(result.path || []).join(" → ")}
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#86868b", textTransform: "uppercase" }}>Per-node</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {(result.trace || []).map((t, i) => (
          <div key={i} style={{ background: "#fafafa", padding: 8, borderRadius: 6, fontSize: 11 }}>
            <div style={{ fontWeight: 600 }}>{t.node_id} <span style={{ color: "#86868b", fontWeight: 400 }}>· {t.kind} · {t.latency_ms}ms</span></div>
            <div style={{ marginTop: 3, color: "#475569", fontFamily: "monospace", fontSize: 10 }}>
              {typeof t.output_preview === "string" ? t.output_preview : JSON.stringify(t.output_preview)}
            </div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#86868b", textTransform: "uppercase" }}>Final</div>
      <div style={{ background: "#EEF0FF", padding: 10, borderRadius: 6, fontSize: 11 }}>
        <div style={{ fontWeight: 600 }}>{result.final_output?.headline}</div>
        {(result.final_output?.what_happened || []).map((x, i) => (
          <div key={i} style={{ marginTop: 4, color: "#1e293b" }}>• {x}</div>
        ))}
        {(result.final_output?.next_actions || []).length > 0 && (
          <div style={{ marginTop: 6, fontSize: 10, color: "#0071E3", fontWeight: 600 }}>
            Next: {(result.final_output.next_actions || []).join(" · ")}
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// UTILITY
// ═══════════════════════════════════════════════════════════════════
function StatusBadge({ status, isActive }) {
  const map = {
    published: { c: "#34C759", bg: "#dcfce7", lbl: "PUBLISHED" },
    draft:     { c: "#0071E3", bg: "#dbeafe", lbl: "DRAFT" },
  };
  const s = map[status] || map.draft;
  return (
    <>
      <span style={{ fontSize: 9, fontWeight: 700, color: s.c, background: s.bg, padding: "2px 6px", borderRadius: 3 }}>
        {s.lbl}
      </span>
      {isActive && (
        <span style={{ fontSize: 9, fontWeight: 700, color: "#166534", background: "#bbf7d0", padding: "2px 6px", borderRadius: 3 }}>
          ● ACTIVE
        </span>
      )}
    </>
  );
}

const input = {
  padding: "6px 10px", border: "0.5px solid #d2d2d7", borderRadius: 6,
  fontSize: 12, fontFamily: "inherit", background: "#fff",
};
const lbl = {
  display: "block", fontSize: 10, fontWeight: 700, textTransform: "uppercase",
  color: "#86868b", letterSpacing: "0.04em", marginBottom: 4,
};
const btnBase = {
  display: "inline-flex", alignItems: "center", gap: 4,
  padding: "6px 10px", fontSize: 11, fontWeight: 600,
  border: 0, borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
};
const btnPrimary = { ...btnBase, background: "#0071E3", color: "#fff" };
const btnAccent  = { ...btnBase, background: "#5856D6", color: "#fff" };
const btnSuccess = { ...btnBase, background: "#065f46", color: "#fff" };
const btnGhost   = { ...btnBase, background: "#fff", color: "#1d1d1f", border: "0.5px solid #e5e5ea" };


// ═══════════════════════════════════════════════════════════════════
// PIPELINE GRAPH VIEW — Feb 2026 (Phase 3 visual canvas)
// react-flow renderer that draws every node as a card and every route
// as a labelled edge. Purely a visualisation swap — clicking any node
// selects it in the same NodeEditor pane on the right, so all editing
// still flows through the existing form. Layout is computed via a
// depth-first walk from the entry node so tree-shaped pipelines
// (classifier → many summarizers) get a natural left-to-right
// hierarchy without a heavyweight dagre dependency.
// ═══════════════════════════════════════════════════════════════════
const NODE_W = 200;
const NODE_H = 76;
const COL_GAP = 90;
const ROW_GAP = 24;

function computeGraphLayout(pipeline) {
  const nodes = pipeline?.nodes || [];
  const entry = pipeline?.entry_node_id;
  if (!nodes.length || !entry) return { positions: {}, depths: {} };
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const depths = {};
  // BFS depth assignment (max distance from entry — handles diamonds).
  const queue = [{ id: entry, d: 0 }];
  while (queue.length) {
    const { id, d } = queue.shift();
    if (!byId.has(id)) continue;
    if (depths[id] !== undefined && depths[id] >= d) continue;
    depths[id] = d;
    const routes = byId.get(id).routes || [];
    for (const r of routes) if (r.to) queue.push({ id: r.to, d: d + 1 });
  }
  // Any orphan nodes (unreachable from entry) → depth 0 to force visibility.
  for (const n of nodes) if (depths[n.id] === undefined) depths[n.id] = 0;
  // Bucket by depth to assign y positions.
  const byDepth = {};
  for (const n of nodes) {
    const d = depths[n.id];
    (byDepth[d] = byDepth[d] || []).push(n.id);
  }
  const positions = {};
  for (const [dStr, ids] of Object.entries(byDepth)) {
    const d = Number(dStr);
    const colX = 20 + d * (NODE_W + COL_GAP);
    ids.forEach((id, i) => {
      positions[id] = { x: colX, y: 20 + i * (NODE_H + ROW_GAP) };
    });
  }
  return { positions, depths };
}

// Custom node renderer — small card with kind badge, name, entry ▶ /
// terminal ■ markers. Handles wired on both sides so edges attach
// naturally in a left→right layout.
function GraphNode({ data, selected }) {
  const isEntry = data.isEntry;
  const isTerminal = data.isTerminal;
  const kind = data.kind;
  const accent = kind === "classifier" ? "#0071E3" : "#34C759";
  return (
    <div
      data-testid={`ai-pipeline-graph-node-${data.id}`}
      style={{
        width: NODE_W, minHeight: NODE_H,
        padding: "8px 10px",
        background: selected ? "#EEF0FF" : "#fff",
        border: `1px solid ${selected ? "#5856D6" : "#e5e5ea"}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 6, fontSize: 11,
        boxShadow: selected ? "0 2px 6px rgba(88,86,214,0.18)" : "0 1px 2px rgba(0,0,0,0.04)",
        cursor: "pointer",
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: "#94a3b8", width: 6, height: 6 }} />
      <Handle type="source" position={Position.Right} style={{ background: "#94a3b8", width: 6, height: 6 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
        <span style={{ fontSize: 9, textTransform: "uppercase", fontWeight: 700, color: accent, letterSpacing: "0.04em" }}>
          {kind === "classifier" ? "Classifier" : "Summarizer"}
        </span>
        {isEntry && <span title="Entry" style={{ fontSize: 9, color: "#5856D6", fontWeight: 700 }}>▶ entry</span>}
        {isTerminal && <span title="Terminal" style={{ fontSize: 9, color: "#34C759", fontWeight: 700 }}>■ end</span>}
      </div>
      <div style={{ fontWeight: 600, fontSize: 12, color: "#1d1d1f", lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis" }}>
        {data.name}
      </div>
      {data.enumValues && data.enumValues.length > 0 && (
        <div style={{ marginTop: 4, fontSize: 9, color: "#64748b", fontFamily: "ui-monospace,monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {data.enumValues.join(" · ")}
        </div>
      )}
    </div>
  );
}

const graphNodeTypes = { pipelineNode: GraphNode };

function PipelineGraphView({ pipeline, selectedNodeId, onSelect }) {
  const { positions } = useMemo(() => computeGraphLayout(pipeline), [pipeline]);
  const rfNodes = useMemo(() => {
    return (pipeline?.nodes || []).map((n) => ({
      id:       n.id,
      type:     "pipelineNode",
      position: positions[n.id] || { x: 20, y: 20 },
      data: {
        id:         n.id,
        name:       n.name,
        kind:       n.kind,
        isEntry:    pipeline?.entry_node_id === n.id,
        isTerminal: !(n.routes && n.routes.length),
        enumValues: n.output_schema?.type === "enum" ? (n.output_schema?.values || []) : null,
      },
      selected: selectedNodeId === n.id,
    }));
  }, [pipeline, positions, selectedNodeId]);

  const rfEdges = useMemo(() => {
    const out = [];
    for (const n of (pipeline?.nodes || [])) {
      for (const r of (n.routes || [])) {
        if (!r.to) continue;
        const label = r.default ? "default" : (r.when || "");
        out.push({
          id:     `${n.id}__${label || "→"}__${r.to}`,
          source: n.id,
          target: r.to,
          label,
          animated: false,
          markerEnd: { type: MarkerType.ArrowClosed, color: r.default ? "#f59e0b" : "#5856D6" },
          style: {
            stroke: r.default ? "#f59e0b" : "#5856D6",
            strokeWidth: 1.5,
            strokeDasharray: r.default ? "5 4" : undefined,
          },
          labelStyle: { fontSize: 10, fontWeight: 600, fill: r.default ? "#92400e" : "#4c46b6" },
          labelBgStyle: { fill: r.default ? "#fef3c7" : "#EEF0FF" },
          labelBgPadding: [3, 5], labelBgBorderRadius: 4,
        });
      }
    }
    return out;
  }, [pipeline]);

  const onNodeClick = useCallback((_, node) => {
    onSelect(node.id);
  }, [onSelect]);

  if (!pipeline?.nodes?.length) {
    return (
      <div style={{ padding: 30, textAlign: "center", color: "#86868b", fontSize: 12 }}>
        Empty pipeline — add a node from the List view.
      </div>
    );
  }

  return (
    <div data-testid="ai-pipeline-graph-view" style={{ flex: 1, minHeight: 420, position: "relative" }}>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={graphNodeTypes}
        onNodeClick={onNodeClick}
        fitView
        fitViewOptions={{ padding: 0.15, maxZoom: 1 }}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} size={1} color="#e5e5ea" />
        <Controls showInteractive={false} />
      </ReactFlow>
      <div style={{ position: "absolute", bottom: 8, right: 12, fontSize: 9, color: "#94a3b8", background: "#fff", padding: "3px 6px", borderRadius: 4, border: "0.5px solid #e5e5ea", display: "flex", gap: 10 }}>
        <span><span style={{ display: "inline-block", width: 12, height: 2, background: "#5856D6", verticalAlign: "middle", marginRight: 4 }} />conditional</span>
        <span><span style={{ display: "inline-block", width: 12, height: 2, background: "#f59e0b", borderTop: "1px dashed", verticalAlign: "middle", marginRight: 4 }} />default</span>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// TEMPLATE PICKER MODAL (Feb 2026 — Phase 3)
// Shown when a super-admin clicks "New Pipeline" instead of the old
// clone-the-system-default flow. Lists starter templates from the
// backend `GET /admin/ai-pipeline-templates` endpoint and instantiates
// the selected one via `POST /admin/ai-pipeline-templates/{id}/instantiate`,
// returning a fresh draft that the caller then opens in the editor.
// ═══════════════════════════════════════════════════════════════════
function TemplatePickerModal({ authHeaders, onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/ai-pipeline-templates`, { headers: authHeaders });
        if (alive) setTemplates(data.templates || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load templates");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [authHeaders]);

  const pick = async (t) => {
    setBusyId(t.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/ai-pipeline-templates/${t.id}/instantiate`,
        {}, { headers: authHeaders },
      );
      toast.success(`Created draft from "${t.name}"`);
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create pipeline from template");
      setBusyId(null);
    }
  };

  return (
    <div
      data-testid="ai-pipeline-template-picker"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 999, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, padding: 24,
          width: "100%", maxWidth: 620, maxHeight: "80vh", overflow: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Start a new pipeline</div>
            <div style={{ fontSize: 12, color: "#86868b", marginTop: 3 }}>
              Pick a starter template. You&apos;ll be dropped into the editor and can
              publish + activate when ready.
            </div>
          </div>
          <button onClick={onClose} data-testid="ai-pipeline-template-picker-close" style={btnGhost}>Close</button>
        </div>

        {loading && <div style={{ padding: 30, textAlign: "center", color: "#86868b" }}>Loading…</div>}

        {!loading && templates.length === 0 && (
          <div style={{ padding: 30, textAlign: "center", color: "#86868b" }}>No templates available.</div>
        )}

        <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
          {templates.map((t) => (
            <button
              key={t.id}
              data-testid={`ai-pipeline-template-${t.id}`}
              onClick={() => pick(t)}
              disabled={busyId !== null}
              style={{
                textAlign: "left", padding: "14px 16px", cursor: busyId ? "wait" : "pointer",
                background: busyId === t.id ? "#EEF0FF" : "#fff",
                border: "0.5px solid #e5e5ea", borderRadius: 10,
                fontFamily: "inherit",
                opacity: busyId && busyId !== t.id ? 0.5 : 1,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 650 }}>{t.name}</div>
                <span style={{ fontSize: 10, color: "#5856D6", background: "#EEF0FF", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>
                  {t.node_count} node{t.node_count === 1 ? "" : "s"}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4, lineHeight: 1.4 }}>{t.description}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// PIPELINE RUNS SECTION (Feb 2026 — Phase 3)
// Collapsible section rendered under the pipeline editor showing the
// last N executions of the pipeline (path, per-node latency, activity
// id, timestamp). Backend endpoint `GET /admin/ai-pipelines/{id}/runs`
// already exists — this is the frontend surface for it.
// ═══════════════════════════════════════════════════════════════════
function PipelineRunsSection({ pipelineId, authHeaders }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/ai-pipelines/${pipelineId}/runs`,
        { params: { limit: 50 }, headers: authHeaders },
      );
      setRuns(data.runs || []);
      setLoaded(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load runs");
    } finally {
      setLoading(false);
    }
  }, [pipelineId, authHeaders]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) load();
  };

  return (
    <div
      data-testid="ai-pipeline-runs-section"
      style={{ marginTop: 20, background: "#fff", border: "0.5px solid #e5e5ea", borderRadius: 10, overflow: "hidden" }}
    >
      <button
        data-testid="ai-pipeline-runs-toggle"
        onClick={toggle}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          width: "100%", padding: "12px 16px", background: "#fafafa",
          border: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <GitBranch size={13} style={{ color: "#5856D6" }} />
          Recent runs {loaded && `(${runs.length})`}
        </span>
        <span style={{ fontSize: 11, color: "#86868b" }}>
          {open ? "▲ Hide" : "▼ Show"}
          {loaded && !loading && (
            <button
              data-testid="ai-pipeline-runs-refresh"
              onClick={(e) => { e.stopPropagation(); load(); }}
              style={{ marginLeft: 8, fontSize: 10, color: "#0071E3", background: "transparent", border: 0, cursor: "pointer", fontFamily: "inherit" }}
            >refresh</button>
          )}
        </span>
      </button>

      {open && (
        <div style={{ padding: "8px 16px 16px" }}>
          {loading && <div style={{ padding: 20, textAlign: "center", color: "#86868b", fontSize: 12 }}>Loading runs…</div>}
          {!loading && loaded && runs.length === 0 && (
            <div data-testid="ai-pipeline-runs-empty" style={{ padding: 20, textAlign: "center", color: "#86868b", fontSize: 12 }}>
              No runs recorded yet. Runs appear here after a manager clicks
              &quot;Review&quot; on a call and the pipeline executes.
            </div>
          )}
          {!loading && runs.length > 0 && (
            <div style={{ display: "grid", gap: 4 }}>
              {runs.map((r, i) => {
                const totalMs = (r.trace || []).reduce((a, t) => a + (t.latency_ms || 0), 0);
                return (
                  <div
                    key={r.id || i}
                    data-testid={`ai-pipeline-run-row-${i}`}
                    style={{
                      display: "grid", gridTemplateColumns: "160px 1fr 80px 90px",
                      gap: 10, padding: "8px 10px",
                      background: i % 2 === 0 ? "#fafafa" : "#fff",
                      border: "0.5px solid #f1f5f9", borderRadius: 6,
                      fontSize: 11, alignItems: "center",
                    }}
                  >
                    <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 10, color: "#475569" }}>
                      {r.ran_at ? new Date(r.ran_at).toLocaleString() : "—"}
                    </div>
                    <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 10, color: "#334155", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {(r.path || []).join(" → ") || <span style={{ color: "#cbd5e1" }}>no path</span>}
                    </div>
                    <div style={{ fontSize: 10, color: "#64748b", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {totalMs > 0 ? `${totalMs}ms` : "—"}
                    </div>
                    <div style={{ fontSize: 10, color: "#5856D6", fontWeight: 600 }}>
                      v{r.pipeline_version || 1}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
